import {useRouteMatch} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {
  Apps,
  Badge,
  Collections,
  RoomService,
  Devices,
  MusicNote,
  People,
  Create,
  Category,
} from '@mui/icons-material';
import {useAuth} from '../../contexts/auth/context';
import {ListItemLink} from '../list-item-link/list-item-link';
import {RolePermissionType} from '../../backend/enums/role-permission-type';
import {ScreenPaths} from '../../enums';
import {Collapse} from '@mui/material';

export interface DigitalInclusionSidePanelComponentsProps {
  showExpanded?: boolean;
  isSubCategoryExpanded?: {menu: string; id: string};
}

export const DigitalInclusionSidePanelComponents = ({
  showExpanded: sidePanelExpanded,
  isSubCategoryExpanded,
}: DigitalInclusionSidePanelComponentsProps) => {
  const {t} = useTranslation();
  const {hasReadPermission, state: authState} = useAuth();
  const {url} = useRouteMatch();
  const listItemsSx = {
    width: '180px',
    height: '40px',
    boxShadow: 'none',
    mx: '11px',
    px: '4px',
    '& .MuiTypography-root': {
      textOverflow: 'ellipsis',
      maxWidth: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      fontSize: '14px',
      fontWeight: 500,
    },
  };
  const dropdownListItemsSx = {
    ...listItemsSx,
    color: '#747474',
    backgroundColor: '#F9FBFF',
    paddingLeft: sidePanelExpanded ? '22px' : '0px',
    paddingRight: '0px',
    '& .MuiTypography-root': {
      fontSize: '12px',
      fontWeight: 500,
      color: '#747474',
    },
    '& .MuiSvgIcon-root': {
      fontSize: '15px',
      color: '#747474',
    },
    '&:hover': {
      filter: 'brightness(95%)',
      backgroundColor: '#F9FBFF',
    },
  };

  return (
    <>
      {hasReadPermission(RolePermissionType.DigitalInclusion_Applications) && (
        <>
          <ListItemLink
            showTooltip={!sidePanelExpanded}
            to={`${url}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Applications}`}
            primary={t(translations.digital_inclusion_side_menu.applications)}
            icon={<Apps />}
            expanded
            teamFirstChild
            sx={listItemsSx}
          />
          <Collapse
            in={isSubCategoryExpanded?.menu === 'applications'}
            collapsedSize={0}
            orientation="vertical"
            timeout={{enter: 500, exit: 500}}
          >
            <ListItemLink
              to={`${url}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Applications}/${isSubCategoryExpanded?.id}/details`}
              primary={t(translations.application_tabs.tabs.details)}
              icon={<Create />}
              teamProfileDropdown
              sx={dropdownListItemsSx}
            />
            <ListItemLink
              to={`${url}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Applications}/${isSubCategoryExpanded?.id}/users`}
              primary={t(translations.application_tabs.tabs.users)}
              icon={<People />}
              teamProfileDropdown
              sx={dropdownListItemsSx}
            />
          </Collapse>
        </>
      )}
      {hasReadPermission(RolePermissionType.DigitalInclusion_Media) && (
        <ListItemLink
          showTooltip={!sidePanelExpanded}
          to={`${url}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Photos}`}
          primary={t(translations.digital_inclusion_side_menu.photos)}
          icon={<Collections />}
          teamMiddleChild
          sx={listItemsSx}
        />
      )}

      {hasReadPermission(RolePermissionType.DigitalInclusion_Cards) && (
        <ListItemLink
          showTooltip={!sidePanelExpanded}
          to={`${url}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Cards}`}
          primary={t(translations.digital_inclusion_side_menu.cards)}
          icon={<Badge />}
          teamMiddleChild
          sx={listItemsSx}
        />
      )}

      {hasReadPermission(RolePermissionType.DigitalInclusion_Devices) && (
        <ListItemLink
          showTooltip={!sidePanelExpanded}
          to={`${url}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Devices}`}
          primary={t(translations.digital_inclusion_side_menu.devices)}
          icon={<Devices />}
          teamMiddleChild
          sx={listItemsSx}
        />
      )}

      {hasReadPermission(RolePermissionType.DigitalInclusion_Services) &&
        authState.session?.selectedAffiliation?.entityServicesAvailable === true && (
          <ListItemLink
            showTooltip={!sidePanelExpanded}
            to={`${url}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Services}`}
            primary={t(translations.digital_inclusion_side_menu.services)}
            icon={<RoomService />}
            teamMiddleChild
            sx={listItemsSx}
          />
        )}

      {hasReadPermission(RolePermissionType.DigitalInclusion_Playlists) && (
        <>
          <ListItemLink
            showTooltip={!sidePanelExpanded}
            to={`${url}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Playlists}`}
            primary={t(translations.digital_inclusion_side_menu.playlists)}
            icon={<MusicNote />}
            teamMiddleChild
            sx={listItemsSx}
          />
          <Collapse
            in={isSubCategoryExpanded?.menu === 'playlists'}
            collapsedSize={0}
            orientation="vertical"
            timeout={{enter: 500, exit: 500}}
          >
            <ListItemLink
              to={`${url}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Playlists}/${isSubCategoryExpanded?.id}/details`}
              primary={t(translations.playlists.professional_caregiver.edit.tabs.details)}
              icon={<Create />}
              teamProfileDropdown
              sx={dropdownListItemsSx}
            />
            <ListItemLink
              to={`${url}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Playlists}/${isSubCategoryExpanded?.id}/items`}
              primary={t(translations.playlists.professional_caregiver.edit.tabs.items)}
              icon={<Category />}
              teamProfileDropdown
              sx={dropdownListItemsSx}
            />
            <ListItemLink
              to={`${url}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Playlists}/${isSubCategoryExpanded?.id}/users`}
              primary={t(translations.playlists.professional_caregiver.edit.tabs.users)}
              icon={<People />}
              teamProfileDropdown
              sx={dropdownListItemsSx}
            />
          </Collapse>
        </>
      )}
    </>
  );
};
